import { useCallback } from "react";

import {
  Action,
  ActionLineType,
  ActionType,
  BasketballActionType,
  HockeyActionType,
  SoccerActionLineType,
  StencilType,
} from "../../types";
import { frameStencilController, getStencilById } from "../../utils";
import { UpdateFrameStencils } from "./use-update-frame-stencils";

export interface UpdateActionTypeProps {
  id: string;
  layerType: ActionLineType;
}
export type UpdateActionTypeFunc = (props: UpdateActionTypeProps) => void;

export const useUpdateActionType = (updateFrameStencils: UpdateFrameStencils): UpdateActionTypeFunc => {
  return useCallback(
    ({ id, layerType }: UpdateActionTypeProps) => {
      // frame stencils set state
      updateFrameStencils((prevState) => {
        const { stencil } = getStencilById({ stencils: prevState || [], id });
        if (!stencil || stencil.stencilType !== StencilType.ACTION) return prevState;

        const updatedAction: Action = ((): Action => {
          switch (layerType) {
            case BasketballActionType.CUT:
            case BasketballActionType.SCREEN:
            case HockeyActionType.SKATE:
            case HockeyActionType.SKATE_BACKWARDS:
            case HockeyActionType.SKATE_LATERAL:
            case SoccerActionLineType.SC_RUN:
            case SoccerActionLineType.SC_RUN_AND_STOP: {
              return {
                ...stencil,
                actionType: ActionType.PLAYER,
                layerType,
                nextPlayerAction: null,
              };
            }

            case BasketballActionType.PASS:
            case HockeyActionType.PASS:
            case HockeyActionType.SHOOT:
            case SoccerActionLineType.SC_PASS:
            case SoccerActionLineType.SC_SHOT: {
              return {
                ...stencil,
                actionType: ActionType.BALL,
                layerType,
                nextBallAction: null,
                nextPlayer: null,
                prevPlayer: null,
                playerActionStart: null,
                playerActionEnd: null,
              };
            }

            case BasketballActionType.DRIBBLE:
            case HockeyActionType.SKATE_WITH_PUCK:
            case HockeyActionType.SKATE_BACKWARDS_WITH_PUCK:
            case SoccerActionLineType.SC_DRIBBLE:
            case SoccerActionLineType.SC_DRIBBLE_AND_STOP: {
              return {
                ...stencil,
                actionType: ActionType.POSSESSION,
                layerType,
                nextPlayerAction: null,
                nextBallAction: null,
              };
            }

            case BasketballActionType.HASHPASS:
            case BasketballActionType.HANDOFF:
            case BasketballActionType.CUT_RECEIVING_HANDOFF:
            case BasketballActionType.SCREEN_RECEIVING_HANDOFF:
            case HockeyActionType.HANDOFF:
            case HockeyActionType.SKATE_RECEIVING_HANDOFF:
            case HockeyActionType.SKATE_BACKWARDS_RECEIVING_HANDOFF:
            case HockeyActionType.SKATE_LATERAL_RECEIVING_HANDOFF:
            case SoccerActionLineType.SC_HANDOFF:
            case SoccerActionLineType.SC_RUN_RECEIVING_HANDOFF:
            case SoccerActionLineType.SC_RUN_AND_STOP_RECEIVING_HANDOFF:
              return stencil;
          }
        })();

        return frameStencilController({ operation: "UPDATE", stencils: prevState, stencil: updatedAction });
      });
    },
    [updateFrameStencils]
  );
};
